import type { FC } from 'react';
import { useEffect, useState } from 'react';
import FileGridItem from './fileitem/FileGridItem';
import FileItemModals from './fileitem/FileItemModals';
import FileListItem from './fileitem/FileListItem';
import {
  formatBytes,
  getImageMimeType,
  getPlaceholder,
  getReadableDate,
  isImageExtension,
} from './fileitem/fileItemUtils';
import { useFileDownload } from './fileitem/hooks/useFileDownload';
import { useFilePermissions } from './fileitem/hooks/useFilePermissions';
import { useFilePreview } from './fileitem/hooks/useFilePreview';
import type { FileItemProps, FileItemViewModel } from './fileitem/types';

const FileItem: FC<FileItemProps> = ({
  view,
  file,
  groups,
  id,
  activeDownloadMenu,
  setActiveDownloadMenu,
  selectItem,
  selectedItems,
}) => {
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const fileExtension = file.file.split('.').pop();
  const dimensions = JSON.parse(file.dimensions);
  const viewModel: FileItemViewModel = {
    dimensions,
    fileExtension,
    isImage: isImageExtension(fileExtension),
    isSvgOrAi: file.file.endsWith('.svg') || file.file.endsWith('.ai'),
    mimeType: getImageMimeType(file.file),
    placeholder: getPlaceholder(fileExtension),
    readableDate: getReadableDate(file.date),
    formattedSize: formatBytes(file.size),
  };

  const { groupColours, visibility } = useFilePermissions(file, groups);
  const { downloadingSize, downloadFile } = useFileDownload();
  const preview = useFilePreview(fileExtension);

  useEffect(() => {
    setShowDownloadMenu(activeDownloadMenu === id);
  }, [activeDownloadMenu, id]);

  const toggleActiveDownloadMenu = (itemId: string) => {
    if (activeDownloadMenu === itemId) {
      setActiveDownloadMenu(null);
    } else {
      setActiveDownloadMenu(itemId);
    }
  };

  const roles = JSON.parse(user.groups || '[]');
  const selected = selectedItems.includes(file.ID);

  return (
    <>
      {view === 'list' ? (
        <FileListItem
          id={id}
          file={file}
          roles={roles}
          selected={selected}
          groupColours={groupColours}
          showDownloadMenu={showDownloadMenu}
          downloadingSize={downloadingSize}
          loadingCropTool={preview.loadingCropTool}
          viewModel={viewModel}
          selectItem={selectItem}
          downloadFile={downloadFile}
          openCropTool={preview.openCropTool}
          toggleActiveDownloadMenu={toggleActiveDownloadMenu}
        />
      ) : (
        <FileGridItem
          id={id}
          view={view}
          file={file}
          roles={roles}
          selected={selected}
          visibility={visibility}
          groupColours={groupColours}
          showDownloadMenu={showDownloadMenu}
          downloadingSize={downloadingSize}
          loadingCropTool={preview.loadingCropTool}
          loadingModal={preview.loadingModal}
          viewModel={viewModel}
          selectItem={selectItem}
          downloadFile={downloadFile}
          openCropTool={preview.openCropTool}
          openModalImage={preview.openModalImage}
          toggleActiveDownloadMenu={toggleActiveDownloadMenu}
        />
      )}
      <FileItemModals
        file={file}
        mimeType={viewModel.mimeType}
        cropImage={preview.cropImage}
        modalImage={preview.modalImage}
        showCropTool={preview.showCropTool}
        showImageModal={preview.showImageModal}
        closeCropTool={preview.closeCropTool}
        setShowImageModal={preview.setShowImageModal}
      />
    </>
  );
};

export default FileItem;
